import { HotKeyBox } from '@modules/common/components/HotKeyIcons/HotKeyBox';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { OptionAltHotkeySymbol } from '@modules/common/components/OptionAltHotkeySymbol';
import { useHotkey } from '@modules/common/hooks';
import { AddIntegrationDiscardConfirmationDialog } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationDiscardConfirmationDialog';
import { KeyboardReturnRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack } from '@mui/material';

interface AddIntegrationChannelDrawerFooterProps {
  onIntegrate: () => void;
  onIntegrateAndAddAnother: () => void;
  onDrawerClose: () => void;
  isIntegrating: boolean;
  isDisabled: boolean;
  shouldOpenCancelConfirmationDialog: boolean;
}

export const AddIntegrationChannelDrawerFooter = ({
  onIntegrate,
  onIntegrateAndAddAnother,
  onDrawerClose,
  isIntegrating,
  isDisabled,
  shouldOpenCancelConfirmationDialog,
}: AddIntegrationChannelDrawerFooterProps) => {
  const { openModal } = useMuiModalContext();

  useHotkey({
    hotkey: 'Alt+Enter',
    onHotkey: () => onIntegrate(),
    isHotKeyDisabled: isDisabled,
    actionTitle: 'Add Integration Channel',
  });

  const onCancel = () => {
    if (shouldOpenCancelConfirmationDialog) {
      openModal({
        contentComponent: () => (
          <AddIntegrationDiscardConfirmationDialog
            onDiscardChanges={onDrawerClose}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      });
    } else {
      onDrawerClose();
    }
  };

  return (
    <Box
      py={2}
      px={3}
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto',
        bottom: 0,
        position: 'sticky',
        backgroundColor: 'misc.drawer.background',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2.5}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <LoadingButton
          variant="outlined"
          color="primary"
          onClick={onIntegrateAndAddAnother}
          disabled={isDisabled}
          loading={isIntegrating}
        >
          Integrate & Add Another
        </LoadingButton>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={onIntegrate}
          disabled={isDisabled}
          loading={isIntegrating}
          endIcon={
            !isIntegrating && (
              <HotKeyBox
                hotkeyDisplay={
                  <Stack direction="row">
                    <OptionAltHotkeySymbol />
                    <KeyboardReturnRounded fontSize="small" />
                  </Stack>
                }
                isDisabled={isDisabled}
              />
            )
          }
        >
          Integrate
        </LoadingButton>
      </Stack>
    </Box>
  );
};
